import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useGetSession, useGetSessionPreview } from "../../../../../hooks/session.hooks";
import { PageModule, PageModuleType, Speaker, } from "../../../../../types/working-model";
import { OptionalComponent } from "../../../../../utils/optional-component";
import { dedupSpeakers } from "../../../../../utils/speakers";

import './speaker-lineup.scss';
import { resizedImage } from "utils/utils";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import Image from "@general-ui/image/image";


interface SpeakersLineupProps {
	setSpeaker?: (speaker: Speaker) => void;
	showSpeakerDetailsModal?: VoidFunction;
	showAllSpeakersFromAllSessions?: boolean
	classNameOverride?: string;
}

export default function SpeakersLineup({
	setSpeaker,
	showSpeakerDetailsModal,
	showAllSpeakersFromAllSessions = false,
	classNameOverride,
}: SpeakersLineupProps): JSX.Element {

	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const event = eventBundle ?? workingEvent;

	const session = useGetSession();
	const sessionPreview = useGetSessionPreview();
	const [speakers, setSpeakers] = useState<Speaker[]>([]);
	const _session = session ?? sessionPreview;

	const syncSpeakersActive = useMemo(() => {
		const sessions = event?.sessions ?? event?.sessionsPreviews;
		if (sessions?.length !== 1) return false;
		return (sessions[0]?.modules ?? []).some(
			(m: PageModule) => m.type === PageModuleType.speakers && m.content?.sync_speakers
		);
	}, [event?.sessions, event?.sessionsPreviews]);

	const getExistingSpeakers = useCallback(() => {
		if ((event?.sessions || event?.sessionsPreviews) && showAllSpeakersFromAllSessions) {
			const _speakers = (event?.sessions || event?.sessionsPreviews || []).map((session) => {
				const speakers = (session?.modules || [])
					.filter((module: PageModule) => (module.type === PageModuleType.speakers && module.is_on && module.content_modules?.length && (!syncSpeakersActive || !module.is_custom)))
					.flatMap(module => module.modules ?? []);
				return speakers;
			}).reduce((acc, val) => acc.concat(val), []);
			const dedupedSpeakers = _speakers.reduce(dedupSpeakers, [[], new Set<number>()])[0];
			return dedupedSpeakers;
		}
		else if (_session?.modules) {
			const speakers = _session.modules
				.filter((module: PageModule) => (module.type === PageModuleType.speakers && module.is_on && module.content_modules?.length && (!syncSpeakersActive || !module.is_custom)))
				.flatMap(module => module.modules ?? []);
			return speakers;
		}
		return [];
	}, [_session?.modules, event, showAllSpeakersFromAllSessions, syncSpeakersActive]);

	useEffect(() => {
		setSpeakers(getExistingSpeakers());
	}, [getExistingSpeakers]);

	const handleSpeakerClick = useCallback((speaker: any) => () => {
		if (!setSpeaker || !showSpeakerDetailsModal) return;
		setSpeaker(speaker);
		showSpeakerDetailsModal();
	}, [setSpeaker, showSpeakerDetailsModal]);

	const uniqueSpeakers = useMemo(() => {
		return speakers.reduce(dedupSpeakers, [[], new Set<number>()])[0];
	}, [speakers]);

	return (

		<OptionalComponent display={!!uniqueSpeakers?.length}>
			<ul className={classNames("session-details-modal-speakers", classNameOverride)}>
				{uniqueSpeakers.map((speaker) => {
					const { speaker: id, first_name, last_name } = speaker;
					return (
						<li key={id}>
							<Image src={resizedImage(speaker.image, { height: 64, width: 64, fit: 'cover' })} fallbackAlt={`Picture of ${speaker.first_name}`} className="speaker-image" />
							<button
								className={classNames("evt-typography-caption-l1 no-padding no-style")}
								onClick={handleSpeakerClick(speaker)}
							>
								{`${first_name} ${last_name}`}
								&nbsp;
							</button>
						</li>
					);
				})}
			</ul>
		</OptionalComponent>


	);
}
